import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    Button,
    ModalNext,
    ModalHeader,
    ModalBody,
    ModalFooter, ToastProvider,
} from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { ViewModelForm, useDataRefresh } from 'gw-portals-viewmodel-react';
import { DocumentComponent } from 'gw-capability-document-react';
import { useValidation } from 'gw-portals-validation-react';
import { FileUtil } from 'gw-portals-util-js';
import { ServiceRequestService } from 'gw-capability-servicerequest';
import messages from './AddInvoicePopup.messages';
import metadata from './AddInvoicePopup.metadata.json5';
import styles from './AddInvoicePopup_AX.module.scss';

function AddInvoicePopup(props) {
    const {
        isOpen,
        onReject,
        onResolve,
        onAfterClose,
        serviceRequestID,
        existingDocuments,
        invoiceSupplementAX,
        viewModelService,
        authHeader
    } = props;
    const [invoiceVM, setInvoiceVM] = useState({});
    const translator = useContext(TranslatorContext);
    const [isLoading, setLoadingState] = useState(false);
    const { refreshData } = useDataRefresh();
    const { isComponentValid, onValidate, registerComponentValidation } = useValidation('AddInvoicePopup');

    useEffect(() => {
        setInvoiceVM(
            viewModelService.create(
                {
                    totalAmount: { amount: 0.1, currency: 'mxn' },
                    description: invoiceSupplementAX ? '.' : undefined
                },
                'cc',
                'edge.capabilities.servicerequest.statement.invoice.dto.ServiceRequestInvoiceDTO'
            )
        );
    // only execute once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validateDocuments = useCallback(() => {
        const documentsArray = _.get(invoiceVM, 'documents.value', []);
        let hasErrors = false;
        if (invoiceSupplementAX) {
            hasErrors = documentsArray.filter(
                (doc) => (!_.isUndefined(doc.ax_InvoiceSupplementDTO)
                && doc.ax_InvoiceSupplementDTO.hasError)
            ).length > 0;
        } else {
            hasErrors = documentsArray.filter(
                (doc) => doc.ax_ServiceRequestStatementDTO.hasError
            ).length > 0;
        }
        return documentsArray.length === 2 && !hasErrors;
    }, [invoiceSupplementAX, invoiceVM]);

    useEffect(() => registerComponentValidation(validateDocuments), [
        registerComponentValidation, validateDocuments
    ]);

    const handleGenericErrorToast = useCallback((error) => {
        ToastProvider.toast({
            message: translator({
                id: 'portal.views.dashboard-table.service-request.GenericBodyError_AX',
                defaultMessage: 'The system wan into an unexpected error, please share this code to the system administrator if the problem persist: Code: {codeNumber} - {codeKey}'
            }, {
                codeNumber: error.errorCode, codeKey: error.baseError
            }),
            autoClose: false,
            type: 'error'
        });
    }, [translator]);

    const handleAddInvoice = useCallback(() => {
        onResolve(invoiceVM);
    }, [invoiceVM, onResolve]);

    const deleteDocumentAX = useCallback((docPublicID) => {
        setLoadingState(true);
        ServiceRequestService.deleteDocumentAX(serviceRequestID, docPublicID, authHeader)
            .then((success) => {
                if (success) {
                    const documentsArray = _.get(invoiceVM, 'documents.value', [])
                        .filter((item) => item.publicID !== docPublicID);
                    const existingDocsToLink = _.get(invoiceVM, 'existingDocumentsToLink.value', [])
                        .filter((item) => item !== docPublicID);
                    _.set(invoiceVM, 'documents.value', [...documentsArray]);
                    _.set(invoiceVM, 'existingDocumentsToLink.value', [...existingDocsToLink]);
                    setInvoiceVM(invoiceVM);
                    refreshData();
                }
            })
            .finally(() => setLoadingState(false));
    }, [authHeader, invoiceVM, refreshData, serviceRequestID]);

    const handleClose = useCallback(() => {
        const documentsArray = _.get(invoiceVM, 'documents.value', []);
        documentsArray.forEach((doc) => deleteDocumentAX(doc.publicID));
        _.set(invoiceVM, 'description', invoiceSupplementAX ? '.' : undefined);
        _.set(invoiceVM, 'documents.value', []);
        setLoadingState(false);
        onReject();
    }, [deleteDocumentAX, invoiceSupplementAX, invoiceVM, onReject]);

    const handleInvoiceVMChange = useCallback((newModel) => {
        setInvoiceVM(newModel);
        refreshData();
    }, [refreshData]);

    const handleUploadDocument = useCallback(async (files) => {
        setLoadingState(true);
        const documentsArray = _.get(invoiceVM, 'documents.value', []);
        const existingDocumentsToLink = _.get(invoiceVM, 'existingDocumentsToLink.value', []);
        const uploadToken = await ServiceRequestService.generateUploadToken(authHeader);
        const dataArray = [];
        const publicIds = [];

        // eslint-disable-next-line no-restricted-syntax
        for (const file of files) {
            const documentMetadataTemplate = {
                serviceRequestId: serviceRequestID,
                name: FileUtil.checkFileName(file.name,
                    [...existingDocuments, ...documentsArray]),
                mimeType: file.type,
                sessionID: uploadToken,
                documentType: (invoiceSupplementAX ? 'AX_InvoiceSupplement' : 'AX_supplier_invoices'),
            };
            let data;
            try {
                // eslint-disable-next-line no-await-in-loop
                data = await ServiceRequestService
                    .uploadDocument(file, documentMetadataTemplate, authHeader);
                if (invoiceSupplementAX && !_.isUndefined(data.ax_InvoiceSupplementDTO)) {
                    _.set(data, 'ax_ServiceRequestStatementDTO.hasError', data.ax_InvoiceSupplementDTO.hasError);
                    _.set(data, 'ax_ServiceRequestStatementDTO.error', data.ax_InvoiceSupplementDTO.error);
                }
                dataArray.push(data);
                publicIds.push(data.publicID);
            } catch (e) {
                handleGenericErrorToast(e);
            }
        }
        _.set(invoiceVM, 'documents.value', [...documentsArray, ...dataArray]);
        _.set(invoiceVM, 'existingDocumentsToLink.value', [...existingDocumentsToLink, ...publicIds]);
        setInvoiceVM(invoiceVM);
        refreshData();
        setLoadingState(false);
    }, [invoiceVM, authHeader, refreshData, serviceRequestID, existingDocuments,
        handleGenericErrorToast, invoiceSupplementAX
    ]);

    const getTitle = useCallback(() => {
        if (invoiceSupplementAX) {
            return translator(messages.addInvoiceSupplementTitle);
        }
        return translator(messages.addInvoiceTitle);
    }, [invoiceSupplementAX, translator]);

    if (_.isEmpty(invoiceVM)) {
        return null;
    }

    const overrides = {
        '@field': {
            showOptional: true
        },
        fileUpload: {
            uploadDocument: handleUploadDocument,
            downloadDocument: ServiceRequestService.downloadDocumentLink,
            isLoading: isLoading,
            displayDateAX: false,
            displayInfoAX: true,
            deleteDocument: deleteDocumentAX
        },
        description: {
            visible: !invoiceSupplementAX
        }
    };

    const resolvers = {
        resolveComponentMap: {
            documentcomponent: DocumentComponent,
        }
    };

    return (
        <ModalNext
            isOpen={isOpen}
            onRequestClose={handleClose}
            onAfterClose={onAfterClose}
            className={styles.mainModalPanel}
        >
            <ModalHeader title={getTitle()} />
            <ModalBody>
                <ViewModelForm
                    uiProps={metadata.componentContent.content}
                    model={invoiceVM}
                    onValidationChange={onValidate}
                    onModelChange={handleInvoiceVMChange}
                    componentMap={resolvers.resolveComponentMap}
                    overrideProps={overrides}
                />
            </ModalBody>
            <ModalFooter>
                <Button
                    type="tertiary"
                    onClick={handleClose}
                >
                    {translator(messages.cancel)}
                </Button>
                <Button
                    onClick={handleAddInvoice}
                    disabled={!isComponentValid}
                >
                    {translator(messages.add)}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
}

AddInvoicePopup.propTypes = {
    isOpen: PropTypes.bool,
    existingDocuments: PropTypes.arrayOf({}),
    serviceRequestID: PropTypes.string,
    onResolve: PropTypes.func,
    onReject: PropTypes.func,
    onAfterClose: PropTypes.func,
};

AddInvoicePopup.defaultProps = {
    onAfterClose: _.noop,
    isOpen: false,
    onResolve: _.noop,
    onReject: _.noop,
    existingDocuments: [],
    serviceRequestID: undefined
};

export default AddInvoicePopup;
